import {
  all,
  put,
  call,
  select,
  take,
  takeEvery,
  takeLatest,
  delay,
} from 'redux-saga/effects';
import { isString } from 'lodash';
import { push } from 'connected-react-router';
import { CLIENT_TYPE, Origin } from '@upstart/harmony/lib/constants';
import {
  NotifyFormat,
  STICKER_FORMAT,
} from '@upstart/harmony/lib/legos/constants';
import { isDefined } from '@upstart/harmony/lib/legos/utils/tsutils';
import { cleanDMSPushErrors } from '@upstart/harmony/lib/error-utils';
import { sendData } from 'util/amplitude';
import { amplitudeEvents } from 'util/amplitudeEvents';
import {
  RECENT_DEAL_LIST_REQUEST,
  ALL_DEALS_REQUEST,
  CAR_REQUEST,
  DEAL_REQUEST,
  DEAL_UPDATE_REQUEST,
  PENCIL_REQUEST,
  PENCIL_CREATE_REQUEST,
  PENCIL_UPDATE_REQUEST,
  INIT_PENCIL,
  PAYMENTS_REQUEST,
  UPDATE_REQUEST_STATUS,
  PENCIL_CRM_PUSH_REQUEST,
  DEAL_CREATE_REQUEST,
  ONLINE_DEAL_CREATE_REQUEST,
  DEAL_CRM_PUSH_REQUEST,
  DEAL_DMS_PUSH_REQUEST,
  DEAL_DMS_PUSH_OFFER_REQUEST,
  SET_PRIMARY_CAR_REQUEST,
  PRINT_STICKER_REQUEST,
  SEND_STICKER_REQUEST,
  FORWARD_TO_FNI_REQUEST,
  LOAD_DEAL_DATA_REQUEST,
  UPDATE_TRADEINS_FOR_DMS,
} from './actionTypes';
import {
  recentDealListSuccess,
  recentDealListFailure,
  allDealsSuccess,
  allDealsFailure,
  dealSuccess,
  dealFailure,
  carSuccess,
  carFailure,
  userSuccess,
  userFailure,
  dealerSuccess,
  dealerFailure,
  pencilSuccess,
  pencilFailure,
  pencilCreateSuccess,
  pencilCreateFailure,
  pencilUpdateSuccess,
  pencilUpdateFailure,
  dealUpdateFailure,
  dealUpdateSuccess,
  paymentsRequest,
  paymentsSuccess,
  paymentsFailure,
  pencilCRMPushSuccess,
  pencilCRMPushFailure,
  selectedCarsSuccess,
  selectedCarsFailure,
  dealCreateFailure,
  dealCreateSuccess,
  dealCRMPushFailure,
  dealCRMPushSuccess,
  dealDMSPushSuccess,
  dealDMSPushFailure,
  dealDMSPushOfferSuccess,
  dealDMSPushOfferFailure,
  setPrimaryCarFailure,
  printStickerSuccess,
  printStickerFailure,
  sendStickerFailure,
  sendStickerSuccess,
  pushingToCRM,
  pushingToDMS,
  forwardDealToFNISuccess,
  forwardDealToFNIFailure,
  setPencilModalText,
  financeResponseUpdateFailure,
} from './actions';
import { pencilListRequest, pencilListPollingReset } from '../pencils/actions';
import { PENCIL_LIST_POLLING_CANCELLED_EVENT } from '../pencils/actionTypes';
import { getDeal } from './selectors';
import DealService from '../../services/DealService';
import DealerService from '../../services/DealerService';
import PencilService from '../../services/PencilService';
import PaymentsService from '../../services/PaymentsService';
import DMSService from '../../services/DMSService';
import { printPDF } from '../../util/printUtils';
import { getPencilWithTradeIns } from '../../util/pencilUtils';
import FinanceResponseService from '../../services/FinanceResponseService';

const POLLING_DELAY = 60000;
const DEBOUNCE_WAIT = 300;

const getPencilUpdateSuccessMsg = deal => {
  if (deal?.origin === Origin.ONLINE) {
    return 'Deal updated successfully';
  }
  return 'Deal has been sent to tablet';
};
const getPencilUpdateFailureMsg = deal => {
  if (deal?.origin === Origin.ONLINE) {
    return 'Failed to update deal';
  }
  return 'Failed to send deal to tablet. Please try again.';
};
const getDMSPushSuccessMsg = (applicant, dmsDealId) => {
  let msg = `${applicant.firstName} ${applicant.lastName}'s deal has been successfully sent to DMS. `;
  if (dmsDealId) {
    msg += `DMS Deal Number: ${dmsDealId}.`;
  }
  return msg;
};

const getDMSPushFailureMsg = error => {
  if (isString(error.message)) {
    return `Failed to push deal to DMS: ${error.message}`;
  }
  return 'Failed to push deal to DMS';
};

const getFinanceResponseUpdateFailureMsg = error => {
  if (isString(error.message)) {
    return `Failed to update trade-in information. Error: ${error.message}`;
  }

  return 'Failed to update trade-in information.';
};

const getDMSPushOfferSuccessMsg = dmsDealId => {
  let msg = `Offer has been successfully sent to DMS. `;
  if (dmsDealId) {
    msg += `DMS Deal Number: ${dmsDealId}.`;
  }
  return msg;
};

const getDMSPushOfferFailureMsg = error => cleanDMSPushErrors(error.message);

function* stopPencilListPolling() {
  // setup the event listener in tandem with the cancellation request
  // so that cancellation events are not missed
  yield all([
    take(PENCIL_LIST_POLLING_CANCELLED_EVENT),
    put(pencilListPollingReset()),
  ]);
}

export function* getRecentDealListTask(action) {
  const { startDate, endDate, leadSource } = action.payload;
  const fetchFn =
    leadSource === Origin.ONLINE
      ? DealService.getOnlineList
      : DealService.getShowroomList;
  try {
    const list = yield call(fetchFn, startDate, endDate);
    yield put(recentDealListSuccess(list));
  } catch (e) {
    yield put(recentDealListFailure(e));
  }
}

export function* getAllDealsTask(action) {
  const { targetServerPage, perPage, search, sort } = action.payload;
  try {
    const list = yield call(
      DealService.getAllDeals,
      targetServerPage,
      perPage,
      search,
      sort,
    );
    yield put(allDealsSuccess(list));
  } catch (error) {
    yield put(allDealsFailure(error));
  } finally {
    yield delay(POLLING_DELAY);
  }
}

export function* getCar(carId) {
  try {
    const car = yield call(DealService.getCar, carId);
    yield put(carSuccess(car));
  } catch (error) {
    yield put(carFailure(error));
  }
}

export function* getCarTask(action) {
  const carId = action.payload;
  yield call(getCar, carId);
}

export function* loadSelectedCars(deal) {
  try {
    // Get selected car ids
    const carIdList = [deal.car_id, ...(deal.secondaryCarIds || [])].filter(
      isDefined,
    );
    const carList = [];
    for (const carId of carIdList) {
      carList.push(yield call(DealService.getCar, carId));
    }
    yield put(selectedCarsSuccess(carList));
  } catch (error) {
    yield put(selectedCarsFailure(error));
  }
}

export function* getUser(userId) {
  try {
    const user = yield call(DealService.getUser, userId);
    yield put(userSuccess(user));
  } catch (error) {
    yield put(userFailure(error));
  }
}

export function* getDealer(dealerId) {
  try {
    const dealer = yield call(DealerService.getDealer, dealerId);
    yield put(dealerSuccess(dealer));
  } catch (error) {
    yield put(dealerFailure(error));
  }
}

export function* getDealTask(action) {
  const { dealId } = action.payload;
  try {
    const deal = yield call(DealService.getDeal, dealId);
    yield put(dealSuccess(deal));
    if (deal?.user_id) {
      yield call(getUser, deal.user_id);
    }
    yield call(loadSelectedCars, deal);
  } catch (error) {
    yield put(dealFailure(error));
  }
}

export function* loadDealData(action) {
  try {
    const { dealId } = action.payload;
    const deal = yield call(DealService.getDeal, dealId);
    yield put(dealSuccess(deal));
    yield call(getDealer, deal.dealer_id);
    yield call(getCar, deal.car_id);
    if (deal?.user_id) {
      yield call(getUser, deal.user_id);
    }
  } catch (error) {
    yield put(dealFailure(error));
  }
}

export function* getPencilTask(action) {
  try {
    const { dealId, pencilId } = action.payload;

    const deal = yield select(getDeal);

    // Consolidate TradeIn Data
    const pencil = getPencilWithTradeIns(
      yield call(PencilService.getPencil, pencilId),
      deal,
    );
    yield call(getCar, pencil.carId);

    yield put(pencilSuccess(pencil));
    yield put(paymentsRequest({ dealId, pencil }));
  } catch (err) {
    yield put(pencilFailure(err));
  }
}

export function* initPencilTask(action) {
  try {
    const { dealId, carId } = action.payload;

    const deal = yield select(getDeal);
    const pencil = getPencilWithTradeIns(
      yield call(PencilService.getDefaultPencil, dealId, carId),
      deal,
    );
    yield put(pencilSuccess(pencil));
    yield put(paymentsRequest({ dealId, pencil }));
  } catch (err) {
    yield put(pencilFailure(err));
  }
}

export function* createPencilTask(action) {
  const deal = yield select(getDeal);
  try {
    yield call(stopPencilListPolling);
    const { pencil, customSuccessMessage } = action.payload;
    const body = { ...pencil, dealId: deal.id };
    const newPencil = yield call(PencilService.createPencil, deal.id, body);
    yield put(
      pencilCreateSuccess({
        pencil: newPencil,
        msg: customSuccessMessage ?? getPencilUpdateSuccessMsg(deal),
      }),
    );
  } catch (err) {
    yield put(
      pencilCreateFailure({
        err,
        msg: getPencilUpdateFailureMsg(deal),
      }),
    );
  }
  yield put(pencilListRequest({ dealId: deal.id }));
}

export function* updatePencilTask(action) {
  const deal = yield select(getDeal);
  try {
    yield call(stopPencilListPolling);
    const { pencil, customSuccessMessage } = action.payload;
    const newPencil = yield call(PencilService.updatePencil, pencil.id, pencil);
    yield put(
      pencilUpdateSuccess({
        pencil: newPencil,
        msg: customSuccessMessage ?? getPencilUpdateSuccessMsg(deal),
      }),
    );
  } catch (err) {
    yield put(
      pencilUpdateFailure({
        err,
        msg: getPencilUpdateFailureMsg(deal),
      }),
    );
  }
  yield put(pencilListRequest({ dealId: deal.id }));
}

export function* pushPencilToCRM(action) {
  const { deal, pencil } = action.payload;
  try {
    yield put(pushingToCRM(true));
    yield call(stopPencilListPolling);
    const result = yield call(PencilService.pushToCRM, deal.id, pencil.id);
    yield put(pencilCRMPushSuccess());
    if (result && result.deskingURL) {
      setTimeout(() => {
        const newWindow = window.open();
        newWindow.opener = null;
        newWindow.location = result.deskingURL;
      }, 0);
    }
  } catch (err) {
    yield put(pencilCRMPushFailure(err));
  } finally {
    yield put(pencilListRequest({ dealId: deal.id }));
    yield put(pushingToCRM(false));
  }
}

export function* updateTradeInsForDMSPush(action) {
  try {
    const { deal, financeResponse } = action.payload;
    yield call(updateDeal, { payload: deal });
    yield call(FinanceResponseService.update, financeResponse);
  } catch (error) {
    yield put(
      financeResponseUpdateFailure(getFinanceResponseUpdateFailureMsg(error)),
    );
  }
}

export function* updateDeal(action) {
  try {
    const deal = action.payload;
    const updatedDeal = yield call(DealService.updateDeal, {
      dealId: deal.id,
      body: deal,
    });
    yield put(dealUpdateSuccess(updatedDeal));
    yield call(loadSelectedCars, updatedDeal);
  } catch (error) {
    yield put(dealUpdateFailure(error));
  }
}

export function* createDeal(action) {
  try {
    const deal = action.payload;
    const createdDeal = yield call(DealService.createDeal, deal);

    // Fetch payments to refresh fees
    yield call(
      PaymentsService.getPaymentsForPencilId,
      createdDeal.id,
      null,
      CLIENT_TYPE.IN_STORE,
    );
    yield put(dealCreateSuccess(createdDeal));
    yield put(push(`/pencil/${createdDeal.id}`));
  } catch (error) {
    yield put(dealCreateFailure(error));
  }
}

export function* createOnlineDeal(action) {
  try {
    const deal = action.payload;
    const createdDeal = yield call(
      DealService.createOnlineDeal,
      deal.dealer_id,
      deal,
    );
    yield put(dealCreateSuccess(createdDeal));
  } catch (error) {
    yield put(dealCreateFailure(error));
  }
}

export function* pushDealToCRM(action) {
  try {
    const deal = action.payload;
    /* We pass this in the request query to avoid forwarding lead to CRM through the update deal endpoint.
     *  And call the endpoints that would create the lead or forward it (in this case, update the lead).
     *  Earlier, updateDeal and createCRMLead would get executed in such quick successions that our system would be able to identify if a lead was created (race condition).
     *  This flag will stop that forwarding, which is mainly for use in Papyrus/In-Store.
     *  REL-14839
     * */
    const forwardLead = false;

    const updatedDeal = yield call(DealService.updateDeal, {
      dealId: deal.id,
      body: deal,
      sendNotification: true,
      forwardLead,
    });

    // Forward lead (Update lead) if required visitID/customerId is present.
    if (updatedDeal?.crmVisitID || updatedDeal?.crmMeta?.customerId) {
      yield call(DealService.forwardCRMLead, {
        body: {
          id: deal.id,
          type: NotifyFormat.NEW_LEAD, // Papyrus provides the same type.
        },
      });
    } else {
      // Create lead if no crmVisitID or customerId in deal.
      yield call(DealService.createCRMLead, deal.id);
    }

    yield put(dealCRMPushSuccess(updatedDeal));
  } catch (error) {
    yield put(dealCRMPushFailure(error));
  }
}

export function* pushDealToDMS(action) {
  try {
    yield put(pushingToDMS(true));
    const { deal, dmsCustomers, taxGroupId } = action.payload;
    const dmsDeal = yield call(
      DMSService.pushToDMS,
      deal,
      dmsCustomers.map(dmsCustomer => dmsCustomer.id),
      taxGroupId,
    );
    const updatedDeal = yield call(DealService.getDeal, deal.id);
    yield put(
      dealDMSPushSuccess({
        deal: updatedDeal,
        msg: getDMSPushSuccessMsg(deal.applicant, dmsDeal?.id),
      }),
    );
  } catch (error) {
    yield put(dealDMSPushFailure(getDMSPushFailureMsg(error)));
  } finally {
    yield put(pushingToDMS(false));
  }
}

export function* pushOfferToDMS(action) {
  try {
    yield put(pushingToDMS(true));
    const { deskingOffer, dmsCustomers, taxGroupId } = action.payload;
    const dmsDeal = yield call(
      DMSService.pushOfferToDMS,
      deskingOffer,
      dmsCustomers.map(dmsCustomer => dmsCustomer.id),
      taxGroupId,
    );
    yield put(
      dealDMSPushOfferSuccess({
        msg: getDMSPushOfferSuccessMsg(dmsDeal?.id),
      }),
    );
  } catch (error) {
    yield put(dealDMSPushOfferFailure(getDMSPushOfferFailureMsg(error)));
  } finally {
    yield put(pushingToDMS(false));
  }
}

export function* setPrimaryCar(action) {
  try {
    const car = action.payload;
    const deal = yield select(getDeal);
    const payload = {
      ...deal,
      car_id: car.id,
      carSource: car.manuallyEnteredSource || '',
      secondaryCarIds: [deal.car_id, ...(deal.secondaryCarIds || [])].filter(
        id => id !== car.id,
      ),
    };

    // Update deal in database
    yield call(updateDeal, { payload });

    // Push to CRM
    yield call(DealService.pushVehicle, deal.id);
  } catch (error) {
    yield put(setPrimaryCarFailure(error));
  }
}

export function* updateRequestStatus(action) {
  try {
    const { deal, type, status } = action.payload;
    const updated = yield call(
      DealService.updateRequestStatus,
      deal.id,
      type,
      status,
    );
    yield put(dealUpdateSuccess(updated));
  } catch (error) {
    yield put(dealUpdateFailure(error));
  }
}

export function* getPaymentsTask(action) {
  try {
    // debouncing https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
    yield delay(DEBOUNCE_WAIT);
    const { dealId, pencilId, pencil, clientType, verbose } = action.payload;
    let payments;
    // if the entire pencil is provided, send it to the backend to calculate payments
    // otherwise use the normal endpoint with generates or reads the pencil from the database
    if (pencil) {
      payments = yield call(
        PaymentsService.getPaymentsForPencilBody,
        dealId,
        pencil,
        clientType || CLIENT_TYPE.IN_STORE, // use instore payments by default
        verbose,
      );
    } else {
      payments = yield call(
        PaymentsService.getPaymentsForPencilId,
        dealId,
        pencilId,
        clientType || CLIENT_TYPE.IN_STORE, // use instore payments by default
        verbose,
      );
    }
    yield put(paymentsSuccess(payments));
  } catch (error) {
    yield put(paymentsFailure(error));
  }
}

export function* printSticker(action) {
  try {
    const pencil = action.payload;
    const { url } = yield call(
      PencilService.getSticker,
      pencil.dealId,
      pencil,
      STICKER_FORMAT.PDF,
    );
    yield put(printStickerSuccess());
    yield call(printPDF, url);
  } catch (error) {
    yield put(printStickerFailure(error));
  }
}

export function* sendSticker(action) {
  try {
    const deal = yield select(getDeal);
    const { pencil, sendParams, configPencilData } = action.payload;
    const body = { ...pencil, dealId: deal.id };

    // Create a pencil with the latest data
    const newPencil = yield call(PencilService.createPencil, deal.id, body);

    // Update finance type for the deal
    const newDeal = { ...deal, financeType: pencil.stickerData.financeType };
    yield call(updateDeal, { payload: newDeal });

    // Send sticker with the given params
    yield call(
      PencilService.sendSticker,
      deal.id,
      newPencil.id,
      sendParams,
      configPencilData,
    );
    sendData(amplitudeEvents.DESKING_PENCIL_SEND_SALESPERSON_FINISH);
    yield put(sendStickerSuccess(newPencil));
  } catch (error) {
    sendData(amplitudeEvents.DESKING_PENCIL_SEND_SALESPERSON_ERROR);
    yield put(sendStickerFailure(error));
  }
}

export function* forwardDealToFNI(action) {
  try {
    yield put(setPencilModalText(''));
    const { dealId, ...options } = action.payload;
    const deal = yield call(DealService.forwardDealToFNI, dealId, options);

    yield put(forwardDealToFNISuccess(deal));
  } catch (error) {
    yield put(forwardDealToFNIFailure(error));
  }
}

export function* dealsSaga() {
  yield takeEvery(RECENT_DEAL_LIST_REQUEST, getRecentDealListTask);
  yield takeEvery(ALL_DEALS_REQUEST, getAllDealsTask);
  yield takeEvery(CAR_REQUEST, getCarTask);
  yield takeEvery(DEAL_REQUEST, getDealTask);
  yield takeEvery(PENCIL_REQUEST, getPencilTask);
  yield takeEvery(INIT_PENCIL, initPencilTask);
  yield takeEvery(PENCIL_CREATE_REQUEST, createPencilTask);
  yield takeEvery(PENCIL_UPDATE_REQUEST, updatePencilTask);
  yield takeEvery(DEAL_UPDATE_REQUEST, updateDeal);
  yield takeLatest(PAYMENTS_REQUEST, getPaymentsTask);
  yield takeEvery(UPDATE_REQUEST_STATUS, updateRequestStatus);
  yield takeEvery(PENCIL_CRM_PUSH_REQUEST, pushPencilToCRM);
  yield takeEvery(DEAL_CREATE_REQUEST, createDeal);
  yield takeEvery(ONLINE_DEAL_CREATE_REQUEST, createOnlineDeal);
  yield takeEvery(DEAL_CRM_PUSH_REQUEST, pushDealToCRM);
  yield takeEvery(DEAL_DMS_PUSH_REQUEST, pushDealToDMS);
  yield takeEvery(DEAL_DMS_PUSH_OFFER_REQUEST, pushOfferToDMS);
  yield takeEvery(SET_PRIMARY_CAR_REQUEST, setPrimaryCar);
  yield takeEvery(PRINT_STICKER_REQUEST, printSticker);
  yield takeEvery(SEND_STICKER_REQUEST, sendSticker);
  yield takeEvery(FORWARD_TO_FNI_REQUEST, forwardDealToFNI);
  yield takeEvery(LOAD_DEAL_DATA_REQUEST, loadDealData);
  yield takeEvery(UPDATE_TRADEINS_FOR_DMS, updateTradeInsForDMSPush);
}
